import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Card,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Image,
  message,
  Popconfirm,
  Row,
  Col,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { getStickerList, getCategoryList, deleteSticker } from '../services/api';
import dayjs from 'dayjs';

const { Option } = Select;

const StickerList = () => {
  const navigate = useNavigate();
  const [stickers, setStickers] = useState([]);
  const [stickerHost, setStickerHost] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({
    keyword: '',
    category_id: undefined,
    file_type: undefined,
    status: undefined,
  });

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadStickers();
  }, [pagination.current, pagination.pageSize]);

  const loadCategories = async () => {
    try {
      const data = await getCategoryList();
      setCategories(data || []);
    } catch (error) {
      console.error('加载分类失败', error);
    }
  };

  const loadStickers = async () => {
    setLoading(true);
    try {
      const data = await getStickerList({
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filters,
      });
      setStickers(data.list || []);
      setStickerHost(data.sticker_host || '');
      setPagination((prev) => ({ ...prev, total: data.total }));
    } catch (error) {
      console.error('加载贴纸失败', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取完整的贴纸URL
  const getStickerUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return stickerHost ? `${stickerHost}/${url}` : url;
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    loadStickers();
  };

  const handleDelete = async (id) => {
    try {
      await deleteSticker(id);
      message.success('贴纸已删除');
      loadStickers();
    } catch (error) {
      console.error('删除失败', error);
    }
  };

  const columns = [
    {
      title: '预览',
      key: 'preview',
      width: 60,
      fixed: 'left',
      render: (_, record) => (
        <Image
          src={getStickerUrl(record.thumbnail_url || record.file_url)}
          alt={record.name}
          width={40}
          height={40}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          preview={{
            src: getStickerUrl(record.file_url),
          }}
        />
      ),
    },
    {
      title: '贴纸名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      ellipsis: true,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ fontSize: 11, color: '#999' }}>
            {record.description ? (record.description.length > 20 ? record.description.slice(0, 20) + '...' : record.description) : ''}
          </div>
        </div>
      ),
    },
    {
      title: '分类',
      key: 'categories',
      width: 120,
      render: (_, record) => (
        <Space size={2} wrap>
          {record.categories?.map((cat) => (
            <Tag key={cat.id} color="blue" style={{ fontSize: 11, margin: 1 }}>
              {cat.name}
            </Tag>
          ))}
          {(!record.categories || record.categories.length === 0) && '-'}
        </Space>
      ),
    },
    {
      title: '类型',
      dataIndex: 'file_type',
      key: 'file_type',
      width: 70,
      render: (type) => (
        <Tag color={type === 'animated' ? 'purple' : 'cyan'} style={{ fontSize: 11 }}>
          {type === 'animated' ? '动图' : '静态'}
        </Tag>
      ),
    },
    {
      title: '状态',
      key: 'status',
      width: 80,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Tag color={record.status === 1 ? 'green' : 'red'} style={{ fontSize: 11 }}>
            {record.status === 1 ? '上架' : '下架'}
          </Tag>
          {record.is_vip_only === 1 && <Tag color="gold" style={{ fontSize: 11 }}>VIP</Tag>}
          {record.is_featured === 1 && <Tag color="magenta" style={{ fontSize: 11 }}>精选</Tag>}
        </Space>
      ),
    },
    {
      title: '统计',
      key: 'stats',
      width: 90,
      render: (_, record) => (
        <Space direction="vertical" size={0} style={{ fontSize: 12 }}>
          <span>
            <EyeOutlined style={{ marginRight: 4 }} />{record.view_count}
          </span>
          <span>
            <DownloadOutlined style={{ marginRight: 4 }} />{record.download_count}
          </span>
        </Space>
      ),
    },
    {
      title: '关键字',
      key: 'keywords',
      width: 130,
      render: (_, record) => (
        <Space size={2} wrap>
          {record.keywords?.slice(0, 3).map((kw) => (
            <Tag key={kw.id} style={{ fontSize: 11, margin: 1 }}>{kw.name}</Tag>
          ))}
          {record.keywords?.length > 3 && (
            <Tag style={{ fontSize: 11, margin: 1 }}>+{record.keywords.length - 3}</Tag>
          )}
          {(!record.keywords || record.keywords.length === 0) && '-'}
        </Space>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: (text) => (
        <span style={{ fontSize: 12 }}>
          {dayjs(text).format('MM-DD HH:mm')}
        </span>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size={0}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => navigate(`/stickers/${record.id}/edit`)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除该贴纸吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>贴纸管理</h2>
        <p>管理贴纸资源，支持上传、编辑、删除贴纸</p>
      </div>

      <Card bordered={false}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="搜索贴纸名称/关键字"
              prefix={<SearchOutlined />}
              value={filters.keyword}
              onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
              onPressEnter={handleSearch}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="选择分类"
              style={{ width: '100%' }}
              allowClear
              value={filters.category_id}
              onChange={(value) => setFilters({ ...filters, category_id: value })}
            >
              {categories.map((cat) => (
                <Option key={cat.id} value={cat.id}>
                  {cat.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="文件类型"
              style={{ width: '100%' }}
              allowClear
              value={filters.file_type}
              onChange={(value) => setFilters({ ...filters, file_type: value })}
            >
              <Option value="static">静态图</Option>
              <Option value="animated">动图</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="状态"
              style={{ width: '100%' }}
              allowClear
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
            >
              <Option value={1}>上架</Option>
              <Option value={0}>下架</Option>
            </Select>
          </Col>
          <Col xs={24} sm={24} md={6}>
            <Space>
              <Button type="primary" onClick={handleSearch}>
                搜索
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/stickers/create')}>
                添加贴纸
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={stickers}
          rowKey="id"
          loading={loading}
          // scroll={{ x: 920, y: 'calc(100vh - 380px)' }}
          scroll={{ x: 920 }}
          size="small"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => setPagination({ ...pagination, current: page, pageSize }),
          }}
        />
      </Card>
    </div>
  );
};

export default StickerList;
